/**
 * Staff API client for the Admin_Portal and Operator_Console.
 *
 * Wraps `createApiClient` from @macksons/contracts (openapi-fetch over the generated `paths`)
 * and adds, per request:
 *  - `Authorization: Bearer <access token>` from the OIDC session;
 *  - `X-Tenant-Id` from the tenant switcher when the caller has not set it and a tenant is
 *    selected (`undefined` = "all tenants", only meaningful for group_admin);
 *  - `Accept-Language` from the current UI locale.
 *
 * `callOperation` invokes a staff operation by operationId using the generated
 * STAFF_OPERATIONS map (method, path, X-Tenant-Id requirement), which lets generic code
 * (the Refine data provider) stay contract-driven. Screens with specific needs use the
 * typed `client.GET('/admin/…')` methods directly.
 */
import { createApiClient, STAFF_OPERATIONS, TENANT_HEADER, } from '@macksons/contracts';
import { unwrap } from './problem.js';
export function createStaffApiClient(opts) {
    const { getTenantId, getLocale } = opts;
    return createApiClient(opts.baseUrl, {
        getAccessToken: opts.getAccessToken,
        ...(opts.fetch ? { fetch: opts.fetch } : {}),
        middleware: [
            {
                onRequest({ request }) {
                    if (!request.headers.has(TENANT_HEADER)) {
                        const tenantId = getTenantId?.();
                        if (tenantId)
                            request.headers.set(TENANT_HEADER, tenantId);
                    }
                    else if (request.headers.get(TENANT_HEADER) === '') {
                        request.headers.delete(TENANT_HEADER);
                    }
                    const locale = getLocale?.();
                    if (locale && !request.headers.has('Accept-Language'))
                        request.headers.set('Accept-Language', locale);
                    return request;
                },
            },
        ],
    });
}
/**
 * Calls a staff operation by operationId and returns the response body (`undefined` for
 * 204), throwing {@link ApiError} for problem responses and network failures.
 */
export async function callOperation(client, operationId, init = {}) {
    const op = STAFF_OPERATIONS[operationId];
    const headers = { ...init.headers };
    // An empty header tells the middleware "explicitly no tenant"; it is removed before sending.
    if (init.tenantId !== undefined)
        headers[TENANT_HEADER] = init.tenantId ?? '';
    const request = client.request;
    return unwrap(request(op.method, op.path, {
        params: { path: init.pathParams ?? {}, query: init.query ?? {}, header: headers },
        ...(init.body === undefined ? {} : { body: init.body }),
        ...(init.signal ? { signal: init.signal } : {}),
    }));
}
//# sourceMappingURL=staff-client.js.map